import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import BackgroundBlobs from '../components/BackgroundBlobs';
import LegalLinks from '../components/LegalLinks';

/**
 * Generic legal document page that fetches a static markdown file from the
 * `/legal/` public folder and renders it with proper, readable typography.
 *
 * The source markdown content is intentionally rendered VERBATIM — we never
 * mutate or rewrite the underlying .md files; only their presentation is
 * styled here.
 */
const LegalPage = ({ title, documentPath, pageTitle }) => {
  const navigate = useNavigate();
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pageTitle) {
      document.title = `${pageTitle} | Harday`;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(documentPath, { cache: 'no-store' });
        if (!res.ok) {
          throw new Error(`Failed to load document (${res.status})`);
        }
        const text = await res.text();
        if (!cancelled) {
          setMarkdown(text);
        }
      } catch (e) {
        if (!cancelled) {
          setError('We could not load this page right now. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [documentPath, pageTitle]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto min-h-screen relative overflow-hidden bg-white">
      <BackgroundBlobs />

      <div className="relative z-10 px-5 sm:px-8 py-6 sm:py-10">
        <motion.button
          onClick={handleBack}
          data-testid="legal-back-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#9333EA] hover:text-[#7C3AED] font-['DM_Sans'] mb-6"
          whileHover={{ x: -2 }}
          whileTap={{ scale: 0.97 }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </motion.button>

        <header className="mb-6">
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#0F172A] font-['Outfit']"
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#FBBF24]" />
        </header>

        {loading && (
          <div className="py-10 text-center text-[#666666] font-['DM_Sans']">
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="py-6 px-4 rounded-[16px] bg-[#FEE2E2] text-[#B91C1C] text-sm font-['DM_Sans']">
            {error}
          </div>
        )}

        {!loading && !error && (
          <article
            className="legal-prose font-['DM_Sans']"
            data-testid="legal-content"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {markdown}
            </ReactMarkdown>
          </article>
        )}

        <div className="mt-12 pt-6 border-t border-gray-200">
          <LegalLinks align="center" tone="muted" />
          <p className="text-[11px] text-center text-[#9CA3AF] mt-2 font-['DM_Sans']">
            © {new Date().getFullYear()} Kings Wake Digital Private Limited
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
